import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";

const TermsOfService: React.FC = () => {
  const navigate = useNavigate();

  usePageMeta(
    "Terms of Service — Ivy",
    "The terms governing use of the Ivy expert interview management platform."
  );

  return (
    <div className="min-h-screen bg-[#FAF9F5] text-[#5B655E] font-schibsted">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-[#FAF9F5]/90 backdrop-blur-sm border-b border-[#E5E1D6]">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-[#5B655E] hover:text-[#1F2823] transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <img src="/Ivy-logo.png" alt="Ivy" className="h-6 w-auto" />
          </button>
          <a
            href="/privacy"
            className="text-sm text-[#5B655E] hover:text-[#0F766E] transition-colors"
          >
            Privacy Policy
          </a>
        </div>
      </nav>

      {/* Content */}
      <div className="container mx-auto px-6 py-16 max-w-3xl">
        <h1 className="font-spectral text-4xl font-medium text-[#1F2823] mb-2">
          Terms of Service
        </h1>
        <p className="text-[#8A938C] mb-12">Last updated: January 29, 2026</p>

        <div className="space-y-10 text-[15px] leading-relaxed">
          <section>
            <h2 className="font-spectral text-xl font-medium text-[#1F2823] mb-3">
              1. Agreement to Terms
            </h2>
            <p>
              These Terms of Service ("Terms") constitute a legally binding
              agreement between you ("User," "you," or "your") and Ivy Digital
              LLC ("Company," "we," "us," or "our") governing your access to and
              use of the Ivy platform at ivytrack.app (the "Service").
            </p>
            <p className="mt-3">
              By accessing or using the Service, you agree to be bound by these
              Terms. If you do not agree, you must not use the Service. If you
              are using the Service on behalf of an organization, you represent
              that you have the authority to bind that organization to these
              Terms.
            </p>
          </section>

          <section>
            <h2 className="font-spectral text-xl font-medium text-[#1F2823] mb-3">
              2. Description of Service
            </h2>
            <p>
              Ivy is an expert interview management platform that provides
              calendar synchronization, AI-powered transcript cleaning, data
              analytics, and a chat interface for querying interview data. The
              Service is designed for professional use by professional services firms and
              similar organizations.
            </p>
          </section>

          <section>
            <h2 className="font-spectral text-xl font-medium text-[#1F2823] mb-3">
              3. Account Registration and Security
            </h2>
            <p>
              You must provide accurate and complete information when creating an
              account. You are responsible for maintaining the confidentiality of
              your login credentials and for all activities that occur under your
              account. You must notify us immediately of any unauthorized use of
              your account.
            </p>
            <p className="mt-3">
              We reserve the right to suspend or terminate accounts that violate
              these Terms, are inactive for an extended period, or pose a
              security risk.
            </p>
          </section>

          <section>
            <h2 className="font-spectral text-xl font-medium text-[#1F2823] mb-3">
              4. Acceptable Use
            </h2>
            <p>You agree not to:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>
                Use the Service for any unlawful purpose or in violation of any
                applicable law or regulation
              </li>
              <li>
                Upload content that is defamatory, obscene, or infringes on the
                rights of any third party
              </li>
              <li>
                Attempt to gain unauthorized access to any part of the Service,
                other accounts, or systems connected to the Service
              </li>
              <li>
                Reverse engineer, decompile, or disassemble any aspect of the
                Service
              </li>
              <li>
                Use the Service to transmit viruses, malware, or other harmful
                code
              </li>
              <li>
                Interfere with or disrupt the integrity or performance of the
                Service
              </li>
              <li>
                Use automated means (bots, scrapers, etc.) to access the Service
                without prior written consent
              </li>
              <li>
                Resell, sublicense, or provide the Service to third parties
                without authorization
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-spectral text-xl font-medium text-[#1F2823] mb-3">
              5. Your Data and Intellectual Property
            </h2>
            <p>
              You retain all ownership rights to the data you upload to the
              Service, including interview transcripts, notes, project
              information, and other content ("Your Data"). We do not claim
              ownership of Your Data.
            </p>
            <p className="mt-3">
              You grant us a limited, non-exclusive license to process, store,
              and transmit Your Data solely to provide and improve the Service.
              This includes transmitting transcript data to third-party AI
              providers for processing as described in our Privacy Policy.
            </p>
            <p className="mt-3">
              You represent and warrant that you have all necessary rights and
              permissions to upload Your Data and that doing so does not violate
              any confidentiality agreement, intellectual property right, or
              applicable law.
            </p>
          </section>

          <section>
            <h2 className="font-spectral text-xl font-medium text-[#1F2823] mb-3">
              6. Company Intellectual Property
            </h2>
            <p>
              The Service, including its design, features, code, documentation,
              logos, and trademarks, is the property of Ivy Digital LLC and is
              protected by intellectual property laws. These Terms do not grant
              you any right to use our trademarks, logos, or branding without
              prior written consent.
            </p>
          </section>

          <section>
            <h2 className="font-spectral text-xl font-medium text-[#1F2823] mb-3">
              7. Third-Party Services
            </h2>
            <p>
              The Service may integrate with third-party providers for
              functionality such as authentication, data storage, and AI
              processing. Your use of these integrations is also subject to the
              respective third party's terms and policies. We are not
              responsible for the practices or availability of any third-party
              service.
            </p>
          </section>

          <section>
            <h2 className="font-spectral text-xl font-medium text-[#1F2823] mb-3">
              8. Service Availability
            </h2>
            <p>
              We strive to maintain high availability of the Service but do not
              guarantee uninterrupted or error-free operation. The Service is
              provided on an "as available" basis. We may modify, suspend, or
              discontinue the Service (or any part of it) at any time, with or
              without notice. We are not liable for any downtime, data loss, or
              service disruption.
            </p>
          </section>

          <section>
            <h2 className="font-spectral text-xl font-medium text-[#1F2823] mb-3">
              9. Payment and Fees
            </h2>
            <p>
              Certain features of the Service may require payment. Fees,
              billing terms, and payment schedules will be communicated to you
              before you incur any charges. All fees are non-refundable unless
              otherwise stated in writing.
            </p>
            <p className="mt-3">
              We reserve the right to change our pricing at any time. We will
              provide at least 30 days' notice before any price change takes
              effect for existing subscribers.
            </p>
          </section>

          <section>
            <h2 className="font-spectral text-xl font-medium text-[#1F2823] mb-3">
              10. Disclaimer of Warranties
            </h2>
            <p className="uppercase">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
              WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY,
              INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
              NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE
              UNINTERRUPTED, ERROR-FREE, SECURE, OR FREE OF VIRUSES OR OTHER
              HARMFUL COMPONENTS.
            </p>
            <p className="mt-3 uppercase">
              WE MAKE NO REPRESENTATIONS OR WARRANTIES REGARDING THE ACCURACY,
              COMPLETENESS, OR RELIABILITY OF ANY AI-GENERATED CONTENT,
              INCLUDING CLEANED TRANSCRIPTS AND CHAT RESPONSES. YOU ARE
              RESPONSIBLE FOR REVIEWING AND VERIFYING ALL AI-PROCESSED OUTPUT.
            </p>
          </section>

          <section>
            <h2 className="font-spectral text-xl font-medium text-[#1F2823] mb-3">
              11. Limitation of Liability
            </h2>
            <p className="uppercase">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT
              SHALL IVY DIGITAL LLC, ITS OFFICERS, DIRECTORS, EMPLOYEES, OR
              AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
              CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO
              LOSS OF PROFITS, DATA, GOODWILL, OR OTHER INTANGIBLE LOSSES,
              ARISING OUT OF OR RELATED TO YOUR USE OF OR INABILITY TO USE THE
              SERVICE, REGARDLESS OF THE THEORY OF LIABILITY.
            </p>
            <p className="mt-3 uppercase">
              OUR TOTAL AGGREGATE LIABILITY FOR ALL CLAIMS ARISING OUT OF OR
              RELATED TO THESE TERMS OR THE SERVICE SHALL NOT EXCEED THE AMOUNT
              YOU PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR
              ONE HUNDRED DOLLARS ($100), WHICHEVER IS GREATER.
            </p>
          </section>

          <section>
            <h2 className="font-spectral text-xl font-medium text-[#1F2823] mb-3">
              12. Indemnification
            </h2>
            <p>
              You agree to indemnify, defend, and hold harmless Ivy Digital LLC
              and its officers, directors, employees, and agents from and
              against any claims, liabilities, damages, losses, and expenses
              (including reasonable attorneys' fees) arising out of or related
              to: (a) your use of the Service; (b) your violation of these
              Terms; (c) your violation of any rights of a third party; or (d)
              Your Data.
            </p>
          </section>

          <section>
            <h2 className="font-spectral text-xl font-medium text-[#1F2823] mb-3">
              13. Termination
            </h2>
            <p>
              You may terminate your account at any time by contacting us. We
              may suspend or terminate your access to the Service at any time,
              with or without cause, and with or without notice.
            </p>
            <p className="mt-3">
              Upon termination, your right to use the Service ceases
              immediately. We will delete Your Data within 30 days of
              termination, unless retention is required by law. Sections 5, 6,
              10, 11, 12, 14, and 15 survive termination.
            </p>
          </section>

          <section>
            <h2 className="font-spectral text-xl font-medium text-[#1F2823] mb-3">
              14. Governing Law and Dispute Resolution
            </h2>
            <p>
              These Terms are governed by the laws of the State of Delaware,
              without regard to its conflict of law principles. Any disputes
              arising from these Terms or the Service shall be resolved
              exclusively in the state or federal courts located in Delaware.
              You consent to the personal jurisdiction of such courts.
            </p>
          </section>

          <section>
            <h2 className="font-spectral text-xl font-medium text-[#1F2823] mb-3">
              15. General Provisions
            </h2>
            <p>
              <span className="font-semibold text-[#1F2823]">Entire Agreement:</span> These Terms,
              together with our Privacy Policy, constitute the entire agreement
              between you and Ivy Digital LLC regarding the Service.
            </p>
            <p className="mt-3">
              <span className="font-semibold text-[#1F2823]">Severability:</span> If any provision
              of these Terms is found to be unenforceable, the remaining
              provisions remain in full force and effect.
            </p>
            <p className="mt-3">
              <span className="font-semibold text-[#1F2823]">Waiver:</span> Our failure to enforce
              any right or provision of these Terms does not constitute a waiver
              of that right or provision.
            </p>
            <p className="mt-3">
              <span className="font-semibold text-[#1F2823]">Assignment:</span> You may not assign
              or transfer your rights under these Terms without our prior written
              consent. We may assign our rights without restriction.
            </p>
          </section>

          <section>
            <h2 className="font-spectral text-xl font-medium text-[#1F2823] mb-3">
              16. Changes to These Terms
            </h2>
            <p>
              We reserve the right to modify these Terms at any time. We will
              notify you of material changes by posting the updated Terms on
              this page and updating the "Last updated" date. Your continued use
              of the Service after changes constitutes acceptance of the updated
              Terms.
            </p>
          </section>

          <section>
            <h2 className="font-spectral text-xl font-medium text-[#1F2823] mb-3">
              17. Contact Us
            </h2>
            <p>
              If you have questions about these Terms, contact us at:
            </p>
            <p className="mt-2">
              Ivy Digital LLC
              <br />
              Email:{" "}
              <a
                href="mailto:support@ivytrack.app"
                className="text-[#0F766E] hover:text-[#0B5D57] underline"
              >
                support@ivytrack.app
              </a>
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 border-t border-[#E5E1D6]">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <span className="text-[#8A938C] text-sm">
              &copy; {new Date().getFullYear()} Ivy Digital LLC
            </span>
            <div className="flex items-center gap-6">
              <a
                href="/privacy"
                className="text-[#8A938C] hover:text-[#0F766E] text-sm transition-colors"
              >
                Privacy Policy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
